import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Building2,
  ClipboardList,
  Plus,
  ArrowRight,
  Landmark,
  FileText,
  CheckCircle2,
  Clock,
  Calculator as CalcIcon,
} from "lucide-react";

export default function ClientDashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === null) nav("/login?next=/area-cliente");
  }, [user, nav]);

  useEffect(() => {
    if (!user || user.role === "admin") return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/dossier/me");
        setDossiers(data || []);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  if (user === undefined) {
    return <div className="max-w-7xl mx-auto p-10 text-ink-muted">Caricamento...</div>;
  }
  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
      {/* Welcome */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand/10 text-brand rounded-full text-[11px] font-display uppercase tracking-[0.2em]">
            <Building2 className="w-3 h-3" /> Area cliente
          </div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl text-ink" data-testid="dashboard-title">
            Ciao <span className="text-brand">{user.referente_nome || user.name || user.email}</span>
          </h1>
          <p className="mt-2 text-sm text-ink-muted">
            {user.ragione_sociale}{" "}
            {user.partita_iva && <span className="font-mono text-xs">· P.IVA {user.partita_iva}</span>}
          </p>
        </div>
        <Link
          to="/calcolatore"
          data-testid="dashboard-cta-simula"
          className="inline-flex items-center gap-2 h-11 px-5 bg-brand text-white rounded font-display uppercase tracking-widest text-sm hover:bg-brand-dark hover:shadow-brand-glow"
        >
          <Plus className="w-4 h-4" /> Nuovo calcolo
        </Link>
      </div>

      {/* Anagrafica card */}
      <div className="mt-8 bg-white border border-line rounded-lg shadow-card p-6">
        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Dati azienda</div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <InfoField label="Ragione sociale" value={user.ragione_sociale} />
          <InfoField label="Partita IVA" value={user.partita_iva} />
          <InfoField label="Referente" value={`${user.referente_nome || ""} ${user.referente_cognome || ""}`.trim()} />
          <InfoField label="Email" value={user.email} />
          <InfoField label="Telefono" value={user.telefono} />
          <InfoField label="Sede" value={[user.via, user.citta].filter(Boolean).join(", ")} />
        </div>
      </div>

      {/* Dossier list */}
      <div className="mt-8">
        <div className="flex items-baseline justify-between">
          <h2 className="font-display text-2xl text-ink">I tuoi dossier</h2>
          <span className="text-sm text-ink-muted">{dossiers.length} totali</span>
        </div>

        {loading ? (
          <div className="mt-4 bg-white border border-line rounded-lg p-10 text-center text-ink-muted">
            Caricamento...
          </div>
        ) : dossiers.length === 0 ? (
          <div className="mt-4 bg-white border border-line rounded-lg p-10 text-center">
            <ClipboardList className="w-10 h-10 text-ink-light mx-auto" />
            <p className="mt-3 text-ink-muted">Non hai ancora aperto nessun dossier.</p>
            <Link
              to="/calcolatore"
              data-testid="empty-cta"
              className="mt-4 inline-flex items-center gap-2 h-11 px-5 bg-brand text-white rounded font-display uppercase tracking-widest text-sm hover:bg-brand-dark"
            >
              <ClipboardList className="w-4 h-4" /> Avvia il primo dossier
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {dossiers.map((d) => (
              <DossierCard key={d.id} d={d} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="mt-0.5 text-ink">{value || <span className="text-ink-muted">—</span>}</div>
    </div>
  );
}

function DossierCard({ d }) {
  const docs = (d.documents || []).length;
  const isComplete = docs > 0;
  const hasCalc = !!d.calc_snapshot;
  return (
    <div
      data-testid={`dossier-card-${d.id}`}
      className="group bg-white border border-line rounded-lg p-5 shadow-card hover:border-brand hover:shadow-lg transition"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          {d.sabatini ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand/10 text-brand rounded text-[11px] font-display uppercase tracking-widest">
              <Landmark className="w-3 h-3" /> Iper + Sabatini
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-line text-ink-muted rounded text-[11px] font-display uppercase tracking-widest">
              <ClipboardList className="w-3 h-3" /> Iperammortamento
            </span>
          )}
          {(d.note || d.cliente?.note) && (
            <div className="mt-2 text-sm text-ink font-medium truncate max-w-[220px]" title={d.note || d.cliente?.note}>
              {d.note || d.cliente?.note}
            </div>
          )}
          <div className="mt-1 font-mono text-[11px] text-ink-muted">
            ID: {d.id.slice(0, 8)}…
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded ${
            isComplete ? "bg-brand/10 text-brand" : "bg-line/60 text-ink-muted"
          }`}
        >
          {isComplete ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
          {isComplete ? "In lavorazione" : "Da compilare"}
        </span>
      </div>

      <div className="mt-4 pt-4 border-t border-line flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 text-ink-muted">
          <FileText className="w-4 h-4" />
          <span>
            {docs} document{docs === 1 ? "o" : "i"} caricat{docs === 1 ? "o" : "i"}
          </span>
        </div>
        <div className="text-ink-muted font-mono text-xs">
          {new Date(d.created_at).toLocaleDateString("it-IT")}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Link
          to={`/checklist/${d.id}`}
          data-testid={`dossier-checklist-${d.id}`}
          className="inline-flex items-center justify-center gap-1.5 h-10 px-3 bg-brand text-white rounded font-display uppercase tracking-widest text-[11px] hover:bg-brand-dark transition"
        >
          <ClipboardList className="w-3.5 h-3.5" /> Check list
        </Link>
        <Link
          to={`/calcolatore?dossier=${d.id}`}
          data-testid={`dossier-riapri-${d.id}`}
          title={
            hasCalc
              ? "Riapre il calcolatore con i valori salvati (calcolo avanzato incluso)"
              : "Apre il calcolatore vuoto (nessun calcolo salvato per questa pratica)"
          }
          className="inline-flex items-center justify-center gap-1.5 h-10 px-3 border border-brand text-brand rounded font-display uppercase tracking-widest text-[11px] hover:bg-brand/10 transition"
        >
          <CalcIcon className="w-3.5 h-3.5" /> Riapri calcolo
        </Link>
      </div>
    </div>
  );
}
